import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/feature/Pagination";
import { works } from "@/mocks/works";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterSidebar from "./components/FilterSidebar";

const PAGE_SIZE = 8;

export default function Works() {
  const [searchParams] = useSearchParams();
  usePageMeta(
    "濑户环奈影视作品分类 - 濑户环奈官网",
    "濑户环奈影视作品全集，涵盖电影与电视剧，支持按类型、年代筛选与排序，快速找到你想看的作品。",
  );

  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") ?? "全部");
  const [yearFilter, setYearFilter] = useState("全部");
  const [sort, setSort] = useState(searchParams.get("sort") ?? "最新");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setKeyword(q);
    const type = searchParams.get("type");
    if (type) setTypeFilter(type);
    const sortParam = searchParams.get("sort");
    if (sortParam) setSort(sortParam);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [keyword, typeFilter, yearFilter, sort]);

  const years = useMemo(
    () => Array.from(new Set(works.map((w) => w.year))).sort((a, b) => b - a),
    [],
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const result = works.filter((work) => {
      const matchKeyword =
        !kw ||
        work.title.toLowerCase().includes(kw) ||
        work.summary.toLowerCase().includes(kw) ||
        work.type.toLowerCase().includes(kw) ||
        work.director.toLowerCase().includes(kw) ||
        work.tags.some((tag) => tag.toLowerCase().includes(kw));
      const matchType = typeFilter === "全部" || work.type === typeFilter;
      const matchYear = yearFilter === "全部" || `${work.year}` === yearFilter;
      return matchKeyword && matchType && matchYear;
    });
    return result.sort((a, b) => (sort === "热门" ? b.hot - a.hot : b.year - a.year));
  }, [keyword, typeFilter, yearFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setKeyword("");
    setTypeFilter("全部");
    setYearFilter("全部");
    setSort("最新");
  };

  const filterPanel = (
    <FilterSidebar
      typeFilter={typeFilter}
      onTypeChange={setTypeFilter}
      yearFilter={yearFilter}
      onYearChange={setYearFilter}
      sort={sort}
      onSortChange={setSort}
      onReset={reset}
      years={years}
      resultCount={filtered.length}
    />
  );

  return (
    <PageShell>
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视作品" }]} />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4 mb-3">
              濑户环奈影视作品
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              收录濑户环奈从出道至今的电影与电视剧作品，可按类型、年代筛选，或按最新、热门排序。
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg bg-background-100 border border-background-200 p-5">
              {filterPanel}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5 flex-1 max-w-md focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-foreground-400 text-base"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索作品名称、类型或标签"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
                {keyword && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清空搜索"
                    className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
            </div>

            {paged.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {paged.map((work) => (
                  <WorkCard key={work.id} work={work} keyword={keyword} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-2xl mb-4">
                  <i className="ri-search-eye-line"></i>
                </span>
                <p className="text-sm text-foreground-500">没有找到符合条件的作品，试试其他关键词或筛选条件。</p>
              </div>
            )}

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFiltersOpen(false)}
          ></div>
          <div className="relative ml-auto w-[85%] max-w-sm h-full bg-background-50 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-5">{filterPanel}</div>
            <div className="sticky bottom-0 p-4 bg-background-50 border-t border-background-200">
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}